import { cropConfigV2Schema } from "@redotech/redo-content-builder-common/crop-config-v2";
import { ImageType } from "@redotech/redo-content-builder-common/image-type";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { categories } from "@redotech/redo-flows-common/triggers";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  contentGeneratorConfigPopulatedSchema,
  contentGeneratorConfigSchema,
} from "./content-generator-config/content-generator-config-definition";
import { ProductSelectionType, TemplateType } from "./email-builder";

// Basic SMS template AI config schema (without populated contentGeneratorConfig)
const smsTemplateAiConfigSchema_ = z.object({
  enabled: z.boolean(),
  contentPreview: z.string().optional(),
  // Until we fully migrate to the new content generator config, we need to allow this to be optional
  contentGeneratorConfigId: zExt.objectId().optional(),
  contentGeneratorConfig: contentGeneratorConfigSchema.optional(),
});
export const smsTemplateAiConfigSchema: z.ZodType<SmsTemplateAiConfig> =
  smsTemplateAiConfigSchema_;

// Populated SMS template AI config schema (with populated contentGeneratorConfig)
const smsTemplateAiConfigPopulatedSchema_ = smsTemplateAiConfigSchema_
  .omit({ contentGeneratorConfigId: true, contentGeneratorConfig: true })
  .extend({
    contentGeneratorConfig: contentGeneratorConfigPopulatedSchema.optional(),
  });
export const smsTemplateAiConfigPopulatedSchema: z.ZodType<SmsTemplateAiConfigPopulated> =
  smsTemplateAiConfigPopulatedSchema_;

export enum SmsAttachmentType {
  IMAGE = "image",
  PRODUCT_IMAGE = "product_image",
  CONTACT_CARD = "contact_card",
}

export type SmsImageAttachment = Extract<
  NonNullable<SmsTemplate["attachments"]>[number],
  { type: SmsAttachmentType.IMAGE | SmsAttachmentType.PRODUCT_IMAGE }
>;

export type SmsContactCardAttachment = Extract<
  NonNullable<SmsTemplate["attachments"]>[number],
  { type: SmsAttachmentType.CONTACT_CARD }
>;

const imageAttachmentSchema = z.object({
  type: z.union([
    z.literal(SmsAttachmentType.IMAGE),
    z.literal(SmsAttachmentType.PRODUCT_IMAGE),
  ]),
  imageSrc: z.string().nullish(),
  cropConfig: cropConfigV2Schema.optional(),
  productSelectionType: createEnum(ProductSelectionType),
  manuallySelectedProduct: z
    .object({
      // used when productSelectionType is MANUAL
      productId: z.string(),
      variantId: z.string(),
    })
    .nullable()
    .optional(),
  productFilterId: zExt.objectId().nullable().optional(), // used when productSelectionType is DYNAMIC and there's no schema variable of type "Cart Context"
  provider: createEnum(Provider).nullish(),
  imageSourceType: createEnum(ImageType).optional(),
});

const contactCardAttachmentSchema = z.object({
  type: z.literal(SmsAttachmentType.CONTACT_CARD),
});

const smsTemplateAttachmentSchema_ = z.union([
  imageAttachmentSchema,
  contactCardAttachmentSchema,
]);
export const smsTemplateAttachmentSchema: z.ZodType<SmsTemplateAttachment> =
  smsTemplateAttachmentSchema_;
export type SmsTemplateAttachment = z.infer<
  typeof smsTemplateAttachmentSchema_
>;

export const smsTemplateSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  content: z.string(),
  outreachProcedureId: zExt.objectId().optional(),
  replyProcedureId: zExt.objectId().optional(),
  prefixContent: z.string().optional(),
  templateType: createEnum(TemplateType),
  category: createEnum(categories),
  schemaType: createEnum(SchemaType),
  autoShortenLinks: z.boolean().optional(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  aiConfig: smsTemplateAiConfigSchema.optional(),
  attachments: z.array(smsTemplateAttachmentSchema).max(2).optional(),
});

export type SmsTemplate = z.infer<typeof smsTemplateSchema>;

/** AI Config with populated contentGeneratorConfig (instead of contentGeneratorConfigId) */
export const smsTemplatePopulatedSchema = smsTemplateSchema
  .omit({ aiConfig: true })
  .extend({ aiConfig: smsTemplateAiConfigPopulatedSchema.optional() });
export type SmsTemplatePopulated = z.infer<typeof smsTemplatePopulatedSchema>;
export type SmsTemplateAiConfig = z.infer<typeof smsTemplateAiConfigSchema_>;
export type SmsTemplateAiConfigPopulated = z.infer<
  typeof smsTemplateAiConfigPopulatedSchema_
>;
